// Pure helpers behind the JSON tree in the mapping editor: where a mapping's
// record-relative field lives in the whole document (and back), and which nodes
// a path/value filter keeps visible. Paths are dotted, like the mapping engine's.
import { resolvePath, type JsonValue } from './mappingEngine';

export const isContainer = (v: JsonValue | undefined): v is JsonValue[] | { [k: string]: JsonValue } => v !== null && typeof v === 'object';

export function childEntries(v: JsonValue): [string, JsonValue][] {
  if (Array.isArray(v)) return v.map((c, i) => [String(i), c]);
  if (isContainer(v)) return Object.entries(v);
  return [];
}

export const joinPath = (parent: string, key: string): string => (parent ? `${parent}.${key}` : key);

// 'a.b.c' -> ['', 'a', 'a.b']: the nodes that must be open to see 'a.b.c'.
export function ancestorsOf(path: string): string[] {
  const parts = path.split('.');
  const out = [''];
  for (let i = 1; i < parts.length; i++) out.push(parts.slice(0, i).join('.'));
  return out;
}

// A mapping rule's `from` (relative to one record) -> the path in the whole
// document: <recordPath>.<index>.<from>. With no recordPath the document is
// either a list of records (<index>.<from>) or one record (<from>).
export function fromToDocPath(from: string, recordPath: string, index: number, docIsList: boolean): string {
  if (recordPath) return `${recordPath}.${index}.${from}`;
  return docIsList ? `${index}.${from}` : from;
}

// The inverse, for clicking a leaf in the tree: a path inside ANY record of the
// list becomes that field's record-relative path (every record has the same
// shape). null = outside the records (e.g. entityName next to the list).
export function docPathToFrom(path: string, recordPath: string, docIsList: boolean): string | null {
  const prefix = recordPath ? `${recordPath}.` : '';
  if (prefix && !path.startsWith(prefix)) return null;
  const rest = path.slice(prefix.length);
  if (!recordPath && !docIsList) return rest; // a single record
  const m = /^(\d+)\.(.+)$/.exec(rest);
  return m ? m[2] : null;
}

export interface FilterResult {
  visible: Set<string>; // matching nodes and all their ancestors
  matches: number;
  capped: boolean;
}

const leafText = (v: JsonValue): string => (v === null ? 'null' : String(v));

// A node matches when its own key or (for a leaf) value contains `filter`,
// case-insensitive; a filter containing a dot is matched against the whole path
// ("us-gaap.Revenues"). Matches keep their ancestors, and a matched container
// keeps its subtree (a few levels) so you can see what is inside. Capped so a
// one-letter filter on a big document stays cheap.
export function filterTree(doc: JsonValue, filter: string, cap = 400): FilterResult {
  const f = filter.trim().toLowerCase();
  const visible = new Set<string>();
  if (!f) return { visible, matches: 0, capped: false };
  const byPath = f.includes('.');
  let matches = 0;
  let capped = false;

  const addSubtree = (v: JsonValue, path: string, depth: number, budget: { n: number }): void => {
    if (depth > 6) return;
    for (const [k, c] of childEntries(v)) {
      if (budget.n-- <= 0) return;
      const p = joinPath(path, k);
      visible.add(p);
      addSubtree(c, p, depth + 1, budget);
    }
  };

  const walk = (v: JsonValue, path: string): void => {
    if (capped) return;
    const key = path.slice(path.lastIndexOf('.') + 1).toLowerCase();
    const hit =
      path !== '' &&
      ((byPath ? path.toLowerCase().includes(f) : key.includes(f)) || (!isContainer(v) && leafText(v).toLowerCase().includes(f)));
    if (hit) {
      matches++;
      for (const a of ancestorsOf(path)) visible.add(a);
      visible.add(path);
      if (isContainer(v)) addSubtree(v, path, 0, { n: 200 });
      if (matches >= cap) {
        capped = true;
        return;
      }
    }
    for (const [k, c] of childEntries(v)) walk(c, joinPath(path, k));
  };
  walk(doc, '');
  return { visible, matches, capped };
}

// The list of records a clicked value belongs to: the longest prefix of `path`
// that is an array in `doc` followed by an index. `rel` is the record-relative
// path (what a rule's `from` needs). null when the value is not inside a list of
// objects (e.g. entityName).
export function listContaining(doc: JsonValue, path: string): { list: string; rel: string } | null {
  const segs = path.split('.');
  for (let i = segs.length - 2; i >= 1; i--) {
    if (!/^\d+$/.test(segs[i])) continue;
    const list = segs.slice(0, i).join('.');
    const v = resolvePath(doc, list);
    if (Array.isArray(v) && v.some((x) => x !== null && typeof x === 'object' && !Array.isArray(x))) return { list, rel: segs.slice(i + 1).join('.') };
  }
  return null;
}
